import { apiClient } from './api';
import {
  ScreenplaySession,
  StartScreenplaySessionRequest,
  StartScreenplaySessionResponse,
  CreateSceneRequest,
  CreateDialogueRequest,
  FormatScreenplayRequest,
  ReviewScreenplayRequest,
  AddScreenplayCharacterRequest,
  AddSceneRequest,
  GenerateScreenplayScriptRequest,
  CreateActDescriptionRequest,
  BulkGenerateScenesRequest,
  ScreenplaySessionStatusResponse,
  ScreenplayActDetailResponse
} from '../models/screenplay';

const STORAGE_KEY = 'screenplay_sessions';

export class ScreenplayService {
  // Local Storage Management
  static loadSessions(): ScreenplaySession[] {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    
    const sessions = JSON.parse(stored);
    // Convert date strings back to Date objects
    return sessions.map((session: any) => ({
      ...session,
      createdAt: new Date(session.createdAt),
      lastUpdatedAt: new Date(session.lastUpdatedAt),
      characters: session.characters.map((char: any) => ({
        ...char,
        createdAt: new Date(char.createdAt)
      })),
      acts: session.acts.map((act: any) => ({
        ...act,
        createdAt: new Date(act.createdAt),
        scenes: act.scenes.map((scene: any) => ({
          ...scene,
          createdAt: new Date(scene.createdAt)
        }))
      }))
    }));
  }

  static saveSession(session: ScreenplaySession): void {
    const sessions = this.loadSessions();
    const index = sessions.findIndex(s => s.id === session.id);
    
    if (index >= 0) {
      sessions[index] = session;
    } else {
      sessions.push(session);
    }
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
  }

  static deleteSession(sessionId: string): void {
    const sessions = this.loadSessions();
    const filtered = sessions.filter(s => s.id !== sessionId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  }

  // Session Management API Methods
  static async startSession(request: StartScreenplaySessionRequest): Promise<StartScreenplaySessionResponse> {
    const response = await apiClient.post('/api/screenplay/session/start', request);
    return response.data;
  }

  static async getSessionStatus(sessionId: string): Promise<ScreenplaySessionStatusResponse> {
    const response = await apiClient.get(`/api/screenplay/session/${sessionId}`);
    return response.data;
  }

  static async getActDetails(sessionId: string, actNumber: number): Promise<ScreenplayActDetailResponse> {
    const response = await apiClient.get(`/api/screenplay/session/${sessionId}/act/${actNumber}`);
    return response.data;
  }

  // Content Generation API Methods
  static async createScene(request: CreateSceneRequest): Promise<any> {
    const response = await apiClient.post('/api/screenplay/scene/create', request);
    return response.data;
  }

  static async createDialogue(request: CreateDialogueRequest): Promise<any> {
    const response = await apiClient.post('/api/screenplay/dialogue/create', request);
    return response.data;
  }

  static async formatScreenplay(request: FormatScreenplayRequest): Promise<any> {
    const response = await apiClient.post('/api/screenplay/format', request);
    return response.data;
  }

  static async reviewScreenplay(request: ReviewScreenplayRequest): Promise<any> {
    const response = await apiClient.post('/api/screenplay/review', request);
    return response.data;
  }

  // Session Modification API Methods
  static async addCharacter(sessionId: string, request: AddScreenplayCharacterRequest): Promise<any> {
    const response = await apiClient.post(`/api/screenplay/session/${sessionId}/character/add`, request);
    return response.data;
  }

  static async addScene(sessionId: string, actNumber: number, request: AddSceneRequest): Promise<any> {
    const url = `/api/screenplay/session/${sessionId}/act/${actNumber}/scene/add`;
    console.log('ScreenplayService.addScene - URL:', url);
    console.log('ScreenplayService.addScene - Request:', request);
    console.log('ScreenplayService.addScene - Headers:', apiClient.defaults.headers);
    
    const response = await apiClient.post(url, request);
    console.log('ScreenplayService.addScene - Response:', response.data);
    return response.data;
  }

  static async generateScript(sessionId: string, request: GenerateScreenplayScriptRequest): Promise<any> {
    const response = await apiClient.post(`/api/screenplay/session/${sessionId}/script/generate`, request);
    return response.data;
  }

  static async createActDescription(sessionId: string, actNumber: number, request: CreateActDescriptionRequest): Promise<any> {
    const response = await apiClient.post(`/api/screenplay/session/${sessionId}/act/${actNumber}/description`, request);
    return response.data;
  }

  static async bulkGenerateScenes(sessionId: string, actNumber: number, request: BulkGenerateScenesRequest): Promise<any> {
    const response = await apiClient.post(`/api/screenplay/session/${sessionId}/act/${actNumber}/scenes/bulk-generate`, request);
    return response.data;
  }

  // Utility Methods
  static exportSession(session: ScreenplaySession): void {
    const dataStr = JSON.stringify(session, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `screenplay-${session.title.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  }

  static exportScript(session: ScreenplaySession, script: string): void {
    const dataUri = 'data:text/plain;charset=utf-8,'+ encodeURIComponent(script);
    
    const exportFileDefaultName = `${session.title.toLowerCase().replace(/\s+/g, '-')}-screenplay.txt`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  }

  static async importSession(file: File): Promise<ScreenplaySession> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const session = JSON.parse(e.target?.result as string);
          // Convert date strings to Date objects
          session.createdAt = new Date(session.createdAt);
          session.lastUpdatedAt = new Date(session.lastUpdatedAt);
          session.characters = session.characters.map((char: any) => ({
            ...char,
            createdAt: new Date(char.createdAt)
          }));
          session.acts = session.acts.map((act: any) => ({
            ...act,
            createdAt: new Date(act.createdAt),
            scenes: act.scenes.map((scene: any) => ({
              ...scene,
              createdAt: new Date(scene.createdAt)
            }))
          }));
          
          // Generate new ID for imported session
          session.id = `imported-${Date.now()}`;
          
          this.saveSession(session);
          resolve(session);
        } catch (error) {
          reject(error);
        }
      };
      reader.readAsText(file);
    });
  }

  // Helper Methods
  static calculateProgress(session: ScreenplaySession): {
    totalProgress: number;
    characterProgress: number;
    actProgress: number;
    sceneProgress: number;
  } {
    const hasCharacters = session.characters.length > 0;
    const hasTreatment = !!session.treatment;
    const totalScenes = session.acts.reduce((sum, act) => sum + act.scenes.length, 0);
    const formattedScenes = session.acts.reduce((sum, act) => 
      sum + act.scenes.filter(s => s.formattedContent).length, 0
    );
    
    const characterProgress = hasCharacters ? 100 : 0;
    const treatmentProgress = hasTreatment ? 100 : 0;
    const sceneProgress = totalScenes > 0 ? (formattedScenes / totalScenes) * 100 : 0;
    const actProgress = session.acts.filter(a => a.description).length / session.acts.length * 100;
    
    const totalProgress = (characterProgress * 0.2 + treatmentProgress * 0.2 + 
                          actProgress * 0.2 + sceneProgress * 0.4);
    
    return {
      totalProgress: Math.round(totalProgress),
      characterProgress,
      actProgress: Math.round(actProgress),
      sceneProgress: Math.round(sceneProgress)
    };
  }

  static formatSceneHeading(location?: string, timeOfDay?: string): string {
    if (!location) return 'UNTITLED SCENE';
    
    let heading = location.toUpperCase();
    if (!heading.startsWith('INT.') && !heading.startsWith('EXT.')) {
      heading = 'INT. ' + heading;
    }
    
    if (timeOfDay) {
      heading += ' - ' + timeOfDay.toUpperCase();
    }
    
    return heading;
  }

  static estimatePageCount(script: string): number {
    // Industry standard: ~1 page per minute, approximately 250 words per page
    const words = script.split(/\s+/).length;
    return Math.ceil(words / 250);
  }
}